<template>
  <modal
    :show="isVisible"
    :title="modalTitle"
    elem-id="modal_origens"
    size="small"
    :close-label="t('Cancelar')"
    :save-label="isDeleteMode ? (isDeleting ? t('Deletando') : t('Deletar')) : (isSaving ? t('Salvando') : t('Salvar'))"
    :save-fn="isDeleteMode ? handleDelete : handleSave"
    @close="handleClose"
    @dismiss="handleClose"
  >
    <!-- Content only renders when modal is visible (prevents API calls on mount) -->
    <template v-if="isVisible">
      <!-- Delete Confirmation Mode -->
      <div v-if="isDeleteMode" class="delete-confirmation">
      <p>{{ t('Tem certeza que deseja deletar esta origem?') }}</p>
      <p class="text-muted">{{ origemToDelete?.origem_nome }}</p>
    </div>

      <!-- Create/Edit Mode -->
      <div v-else>
        <origens-form
          ref="formRef"
          :origemId="currentOrigemId"
          :isEditMode="isEditMode"
          @submit-success="handleFormSuccess"
          @submit-error="handleFormError"
        />
      </div>
    </template>
  </modal>
</template>

<script setup lang="ts">
import { ref, computed, nextTick, onMounted, onBeforeUnmount } from 'vue';
import { useI18n } from 'vue-i18n';
import { useOrigens } from '../../composables/useOrigens';
import { eventBus } from '../../eventBus';
import OrigensForm from './OrigensForm.vue';
import { log, warn as logWarn, error as logError } from '../../utils/DebugLogger';

type OrigemId = number | string;

interface OrigemRecord {
  origem_id?: number;
  origem_nome?: string;
  [key: string]: unknown;
}

interface OrigensFormInstance {
  resetForm: () => void;
  loadForEdit: () => Promise<void>;
  handleSubmit: () => Promise<void>;
  validateForm: () => boolean;
}

interface ModalOpenPayload {
  id?: OrigemId;
  action?: 'create' | 'edit' | 'delete';
  origemData?: OrigemRecord | null;
}

// Get i18n composable
const { t } = useI18n();

// Use composables
const { deleteOrigem } = useOrigens();

// Reactive data
const isVisible = ref<boolean>(false);
const isEditMode = ref<boolean>(false);
const isDeleteMode = ref<boolean>(false);
const currentOrigemId = ref<OrigemId | null>(null);
const origemToDelete = ref<OrigemRecord | null>(null);
const formRef = ref<OrigensFormInstance | null>(null);
const isSaving = ref<boolean>(false);
const isDeleting = ref<boolean>(false);

// Computed properties
const modalTitle = computed<string>(() => {
  if (isDeleteMode.value) {
    return t('Deletar Origem');
  } else if (isEditMode.value) {
    return t('Editar Origem');
  } else {
    return t('Adicionar Origem');
  }
});

// Methods
const openForCreate = (): void => {
  log('OrigensModal', 'openForCreate', 'Opening modal for create');
  isVisible.value = true;
  isEditMode.value = false;
  isDeleteMode.value = false;
  currentOrigemId.value = null;

  // Reset form after modal is visible
  nextTick(() => {
    if (formRef.value) {
      formRef.value.resetForm();
    }
  });
};

const openForEdit = async (origemId: OrigemId): Promise<void> => {
  log('OrigensModal', 'openForEdit', 'Opening modal for edit', origemId);
  isVisible.value = true;
  isEditMode.value = true;
  isDeleteMode.value = false;
  currentOrigemId.value = origemId;

  // Wait for the form component to be rendered
  await nextTick();

  // Explicitly load data for edit
  if (formRef.value) {
    await formRef.value.loadForEdit();
  }
};

const openForDelete = async (origemId: OrigemId, origemData: OrigemRecord | null = null): Promise<void> => {
  log('OrigensModal', 'openForDelete', 'Opening modal for delete', origemId);
  isVisible.value = true;
  isEditMode.value = false;
  isDeleteMode.value = true;
  currentOrigemId.value = origemId;
  origemToDelete.value = origemData;
};

const handleClose = (): void => {
  log('OrigensModal', 'handleClose', 'Closing modal');
  isVisible.value = false;
  isEditMode.value = false;
  isDeleteMode.value = false;
  currentOrigemId.value = null;
  origemToDelete.value = null;
  isSaving.value = false;
  isDeleting.value = false;

  // Reset form
  if (formRef.value) {
    formRef.value.resetForm();
  }
};

const handleSave = async (): Promise<void> => {
  log('OrigensModal', 'handleSave', 'Save button clicked');
  if (formRef.value) {
    isSaving.value = true;
    try {
      await formRef.value.handleSubmit();
    } finally {
      isSaving.value = false;
    }
  }
};

const handleDelete = async (): Promise<void> => {
  if (!currentOrigemId.value) {
    logWarn('OrigensModal', 'handleDelete', 'No origem ID to delete');
    return;
  }

  log('OrigensModal', 'handleDelete', 'Deleting origin', currentOrigemId.value);
  isDeleting.value = true;

  try {
    const idAsNumber = typeof currentOrigemId.value === 'string'
      ? parseInt(currentOrigemId.value, 10)
      : currentOrigemId.value;
    await deleteOrigem(idAsNumber);
    eventBus.emit('globalMessage.success', t('Origem deletada com sucesso'));
    eventBus.emit('origem.deleted');
    handleClose();
  } catch (err) {
    const error = err as Error;
    logError('OrigensModal', 'handleDelete', 'Error deleting origin', error);
    eventBus.emit('globalMessage.error', t('Erro ao deletar origem') + ': ' + error.message);
  } finally {
    isDeleting.value = false;
  }
};

const handleFormSuccess = (): void => {
  log('OrigensModal', 'handleFormSuccess', 'Form submitted successfully');
  eventBus.emit('origem.saved');
  handleClose();
};

const handleFormError = (error: unknown): void => {
  logError('OrigensModal', 'handleFormError', 'Form submission error', error);
  // Error message is already shown by the form component
};

// Standard modal open handler (for modal.origens.open event)
const handleModalOpen = (data?: ModalOpenPayload): void => {
  log('OrigensModal', 'handleModalOpen', 'Standard modal open event received', data);

  if (data?.action === 'delete' && data?.id) {
    void openForDelete(data.id, data.origemData || null);
  } else if (data?.id) {
    void openForEdit(data.id);
  } else {
    openForCreate();
  }
};

// Lifecycle - Event listeners
onMounted(() => {
  log('OrigensModal', 'onMounted', 'Component mounted, setting up event listeners');

  // Standard modal events (preferred pattern)
  eventBus.on('modal.origens.open', handleModalOpen);
  eventBus.on('modal.origens.close', handleClose);
});

onBeforeUnmount(() => {
  log('OrigensModal', 'onBeforeUnmount', 'Component unmounting, cleaning up event listeners');

  // Remove standard modal events
  eventBus.off('modal.origens.open', handleModalOpen);
  eventBus.off('modal.origens.close', handleClose);
});

// Expose methods for parent components (legacy support)
defineExpose({
  openForCreate,
  openForEdit,
  openForDelete
});
</script>

<style scoped>
.delete-confirmation {
  padding: 20px;
  text-align: center;
}

.delete-confirmation p {
  margin-bottom: 10px;
  font-size: 16px;
}

.text-muted {
  color: #6c757d;
  font-style: italic;
}

</style>
